import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { OptionModel } from 'services/models/model';

import { FileService } from '@core/services/file.service';
import { AFDModel } from '../models/clock/afd.model';

import { environment } from 'environments/environment';
import { HttpService } from '../http.service';
import { CompanyModel } from '../models/company.model';
import { TimeShiftModel } from '../models/hiring/timeShift.model';
import { Filter } from '../models/odata/filter';
import { QueryParams } from '../models/odata/queryParams';
import { Response } from '../models/response';
import { ServiceBase } from '../servicebase';
import { AdmissionUrlsModel } from 'services/models/admissionDocs.model';
import { HttpBackend, HttpClient, HttpEvent } from '@angular/common/http';

export type FileToPreSignedUrlResponse = {
  url: string;
  fileKey: string;
};

@Injectable()
export class CompanyService extends ServiceBase<CompanyModel> {
  private httpClientNoAuth: HttpClient;

  constructor(
    http: HttpService,
    private file: FileService,
    handler: HttpBackend
  ) {
    super('company', http, environment.apiUrl);
    this.httpClientNoAuth = new HttpClient(handler);
  }

  createEntity(input: any): CompanyModel {
    return CompanyModel.create(input);
  }

  setStatus(id: string, enable: boolean): Observable<Response<any>> {
    return this.http.put(`${this.endpoint}/status`, { id, enable }).pipe(
      map(res => {
        const response = new Response<any>();
        response.data = res;
        return response;
      })
    );
  }

  getCompaniesOptions(
    disableLoading: boolean = false,
    includeOfficeType: boolean = true
  ): Observable<OptionModel[]> {
    return new Observable<OptionModel[]>(observer => {
      this.list(
        {
          select: ['name', 'id', 'headOfficeId', 'companyGroupId'],
          orderBy: 'name',
          orderByOrder: 'asc',
          filter: Filter.equal('enable', true),
        } as QueryParams,
        disableLoading
      ).subscribe(res => {
        observer.next(
          res.data
            .filter(x => x.enable)
            .map(c =>
              includeOfficeType
                ? c.toOption()
                : ({
                  label: c.name,
                  value: c.id,
                } as OptionModel)
            )
        );
        observer.complete();
      });
    });
  }

  downloadAFD(afd: AFDModel): Observable<Blob> {
    return this.http.post(`${this.endpoint}/create.afd`, afd).pipe(
      map(res => {
        const blobData = res['fileContents'];
        const blob = this.file.base64ToBlob(blobData);
        const tipo = 'txt';
        this.file.saveFile(blob, `${res['fileDownloadName']}.${tipo}`);
        return blob;
      })
    );
  }

  loadTimeShifts(companyId: string): Observable<TimeShiftModel[]> {
    return new Observable<any>(observer => {
      (this.customList<TimeShiftModel>(
        {
          select: ['code', 'title'],
          orderBy: 'code',
          orderByOrder: 'asc',
          filter: Filter.equalToGuid('companyId', companyId),
        } as QueryParams,
        'list.timeshitfs',
        TimeShiftModel,
        true
      ).subscribe(res => {
        observer.next(res.data.filter(x => x.enable));
      }),
        err => {
          observer.error(err);
        });
    });
  }

  listByHierarchy(): Observable<Response<CompanyModel[]>> {
    return this.http
      .get(`${this.endpoint}/list-by-hierarchy`, undefined, undefined, undefined, true)
      .pipe(
        map(res => {
          const response = new Response<CompanyModel[]>();
          response.data = res.body.filter(c => c.enable).map(pp => CompanyModel.create(pp));
          response.count = res.headers.get('x-count');
          return response;
        })
      );
  }

  listSimpleCompanies(): Observable<Response<CompanyModel[]>> {
    const params = {
      select: ['name', 'id', 'headOfficeId', 'cnpj'],
      orderBy: 'name',
      orderByOrder: 'asc',
    } as QueryParams;
    const defaultSelect = ['id', 'enable', 'creationDate'];
    params.select = [
      ...params.select.filter(field => !defaultSelect.includes(field)),
      ...defaultSelect,
    ];

    const query = this.createBuildQuery(params);
    return this.http
      .get(`${this.endpoint}/odata${query}`, undefined, this.baseUrl, undefined, true)
      .pipe(
        map(res => {
          const response = new Response<CompanyModel[]>();
          response.data = res.body
            .filter(c => c.enable)
            .map(pp => this.createEntity(pp) as CompanyModel);
          response.count = res.headers.get('x-count');
          return response;
        })
      );
  }

  getListFilterd(params?: any): Observable<OptionModel[]> {
    const queryParams = `?orderBy=name&orderByOrder=asc&${params.join('&')}`;

    return this.http
      .get(`${this.endpoint}/odata${queryParams}`, null, null, null, false)
      .pipe(map((data: any) => data.body.map(item => CompanyModel.create(item).toOption())));
  }

  getAdmissionFilesByCompanyId(companyId: string): Observable<AdmissionUrlsModel> {
    return this.http
      .get(`${this.endpoint}/admission-files-by-company/${companyId}`, undefined, this.baseUrl)
      .pipe(
        map(res => {
          return AdmissionUrlsModel.create(res.body);
        })
      );
  }

  uploadFileToPreSignedUrlWithProgress(
    file: File,
    featureFolder: string
  ): Observable<{ event: HttpEvent<any>; url: string; fileKey: string }> {
    return this.getPreSignedUrlFile(file.name, file.type, featureFolder).pipe(
      switchMap((data: FileToPreSignedUrlResponse) => {
        return this.httpClientNoAuth
          .put(data.url, file, {
            headers: {
              'Content-Type': file.type,
            },
            withCredentials: false,
            observe: 'events',
            reportProgress: true,
          })
          .pipe(
            map((event: HttpEvent<any>) => ({
              event,
              url: data.url,
              fileKey: data.fileKey,
            }))
          );
      })
    );
  }

  getPreSignedUrlFile(fileName: string, fileType: string, featureFolder: string) {
    const query = `fileName=${fileName}&contentType=${fileType}&featureFolder=${featureFolder}`;

    return this.http
      .get(`${this.endpoint}/pre-signed-url-put?${query}`, null, null, null, true)
      .pipe(
        map((res: any) => {
          return res.body;
        })
      );
  }

  getCompaniesByCompanyRootId(companyRootId: string): Observable<Response<CompanyModel[]>> {
    const filters = [];
    filters.push(Filter.buildFilter('companyRootId', companyRootId));
    filters.push(Filter.buildFilter('enable', true));

    const params = {
      select: ['id', 'name', 'companyRootId', 'enable'],
      orderBy: 'name',
      orderByOrder: 'asc',
      filter: Filter.multEquals(filters),
    } as QueryParams;

    return this.list(params, true);
  }

  getCompaniesByCompanyRootIds(companyRootIds: string[]): Observable<Response<CompanyModel[]>> {
    if (!companyRootIds || companyRootIds.length === 0) {
      return new Observable<Response<CompanyModel[]>>(observer => {
        const response = new Response<CompanyModel[]>();
        response.data = [];
        response.count = 0;
        observer.next(response);
        observer.complete();
      });
    }

    const companyRootFilter = companyRootIds
      .filter(id => !!id)
      .map(id => `companyRootId eq ${id}`)
      .join(' or ');

    if (!companyRootFilter) {
      return new Observable<Response<CompanyModel[]>>(observer => {
        const response = new Response<CompanyModel[]>();
        response.data = [];
        response.count = 0;
        observer.next(response);
        observer.complete();
      });
    }

    const filters = [];
    filters.push(`(${companyRootFilter})`);
    filters.push(Filter.buildFilter('enable', true));

    const params = {
      select: ['id', 'name', 'companyRootId', 'enable'],
      orderBy: 'name',
      orderByOrder: 'asc',
      filter: Filter.build('', filters.join(' and ')),
    } as QueryParams;

    return this.list(params, true);
  }
}
